import { parseServerDate } from './server-date';
// Hàm thuần cho "Chấm điểm và uy tín" (M5, Task 3) — kiểm form chấm điểm, nhãn sao/"Mới", đếm hạn
// chấm điểm. Tách khỏi component để test không cần dựng React. ĐÚNG câu chữ + thứ tự với
// `SubmitBrandRatingHandler`/`BrandReputationRules` phía payment (xem BrandRatingHandlers.cs,
// BrandDealConst) — không báo lỗi khác với backend khi gửi.

export const RATING_LIMITS = {
  starsMin: 1,
  starsMax: 5,
  lowStarsThreshold: 3,
  commentMinLength: 10,
  commentMaxLength: 500,
} as const;

/** Khớp `BrandDealConst.RatingReviewsPageSize` phía payment (giá trị 20) — hồ sơ điểm công khai
 * (trang hồ sơ đánh giá) dùng để biết có còn trang sau hay không (trang trả đủ số này). */
export const RATING_REVIEWS_PAGE_SIZE = 20;

export interface RatingFormInput {
  stars: number;
  comment: string;
}

/**
 * Kiểm form chấm điểm — trả `null` khi hợp lệ, ngược lại câu lỗi tiếng Việt ĐÚNG với
 * `SubmitBrandRatingHandler.Handle` phía payment: sao 1–5 trước, rồi nhận xét theo số sao (≤3 sao
 * bắt buộc 10–500 ký tự, ≥4 sao tối đa 500 ký tự nếu có viết).
 */
export const validateRating = (input: RatingFormInput): string | null => {
  const { starsMin, starsMax, lowStarsThreshold, commentMinLength, commentMaxLength } = RATING_LIMITS;
  if (!Number.isInteger(input.stars) || input.stars < starsMin || input.stars > starsMax)
    return 'Số sao từ 1 đến 5.';

  const comment = (input.comment ?? '').trim();
  if (input.stars <= lowStarsThreshold) {
    if (comment.length < commentMinLength || comment.length > commentMaxLength)
      return 'Chấm từ 3 sao trở xuống cần nhận xét từ 10 đến 500 ký tự.';
  } else if (comment.length > commentMaxLength) {
    return 'Nhận xét tối đa 500 ký tự.';
  }

  return null;
};

/** `minCount` mặc định 3 — khớp SystemConfig `brandRating.minCount` (global-constraints), FE không có
 * endpoint riêng để tải ngưỡng này nên hard-code, chỉ đổi theo nếu admin đổi cấu hình và BE expose thêm. */
const DEFAULT_MIN_COUNT = 3;

/**
 * Nhãn điểm uy tín hiện trên thẻ/danh bạ — dưới `minCount` lượt (chưa đủ dữ liệu) ⇒ "Mới"; ngược lại
 * làm tròn 1 chữ số thập phân (dùng dấu phẩy Việt Nam) + số lượt trong ngoặc, vd "4,6 ★ (12)".
 * `Math.round` (không phải `toFixed` trực tiếp) để tránh lỗi làm tròn số thực (vd 4.55 → "4.5" do
 * biểu diễn dấu phẩy động, trong khi 4.55 làm tròn 1 chữ số phải ra 4.6).
 */
export const ratingBadge = (average: number | null | undefined, count: number, minCount = DEFAULT_MIN_COUNT): string => {
  if (average == null || count < minCount) return 'Mới';
  const rounded = Math.round(average * 10) / 10;
  return `${rounded.toFixed(1).replace('.', ',')} ★ (${count})`;
};

/**
 * Nhãn sao trung bình KHÔNG kèm số lượt (vd "4,6 ★") — dùng ở bảng admin "Uy tín" (tab Task 4), nơi số
 * lượt đã có cột riêng. Chưa có average (chưa từng được chấm) ⇒ "—" (khác `ratingBadge`, trang admin
 * tra cứu được subject bất kỳ nên không gán nhãn "Mới" cho trường hợp này).
 */
export const avgStarsLabel = (average: number | null | undefined): string => {
  if (average == null) return '—';
  const rounded = Math.round(average * 10) / 10;
  return `${rounded.toFixed(1).replace('.', ',')} ★`;
};

/** Lý do miễn trừ (admin) — CÙNG giới hạn + CÙNG câu lỗi phía payment
 * (`BrandDealConst.RatingExemptReasonInvalidMessage`, xem BrandReputationAdminHandlers.cs). */
export const RATING_EXEMPT_REASON_LIMITS = { minLength: 10, maxLength: 500 } as const;

/** null = hợp lệ, ngược lại câu lỗi — khớp `BrandDealConst.RatingExemptReasonInvalidMessage`. */
export const validateRatingExemptReason = (reason: string | null | undefined): string | null => {
  const { minLength, maxLength } = RATING_EXEMPT_REASON_LIMITS;
  const text = reason?.trim() ?? '';
  if (text.length < minLength || text.length > maxLength) return 'Lý do từ 10 đến 500 ký tự.';
  return null;
};

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;

/**
 * Đếm thời gian còn lại tới `dueAtIso` (hạn chấm điểm, mở chấm + 30 ngày) — từ 1 ngày trở lên hiện
 * ngày (làm tròn lên), dưới 1 ngày hiện giờ (làm tròn lên), hết hạn/không có hạn hiện câu lỗi ĐÚNG với
 * `BrandDealConst.RatingExpiredMessage` phía payment.
 */
export const ratingDueLabel = (dueAtIso: string | null | undefined, nowMs: number): string => {
  if (!dueAtIso) return 'Đã hết hạn chấm điểm.';
  const due = parseServerDate(dueAtIso)?.getTime() ?? NaN;
  if (!Number.isFinite(due)) return 'Đã hết hạn chấm điểm.';
  const diffMs = due - nowMs;
  if (diffMs <= 0) return 'Đã hết hạn chấm điểm.';
  if (diffMs < DAY_MS) return `Còn ${Math.ceil(diffMs / HOUR_MS)} giờ để chấm điểm`;
  return `Còn ${Math.ceil(diffMs / DAY_MS)} ngày để chấm điểm`;
};

/** Lỗi "Bạn cần chấm điểm các deal đã xong trước khi làm việc mới." (global-constraints) — CÂU DUY
 * NHẤT chứa đúng cụm này, dùng để quyết có hiện nút "Chấm điểm ngay" (chuyển tab) trên toast lỗi hay
 * không. */
export const isNeedToRateReason = (reason: string): boolean => reason.includes('Bạn cần chấm điểm');
